'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { categories } from '@/data/categories';

const navLinks = [
  { label: 'Home', href: '/homepage' },
  { label: 'Products', href: '/products' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [pathname]);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-corp-navy text-white text-xs py-2 px-4 hidden md:block">
        <div className="max-w-container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6 text-gray-300">
            <a href="mailto:sales@instrutech.in" className="hover:text-white transition-colors flex items-center gap-1.5" aria-label="Email Instrutech Solutions">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              instrutechsouth@gmail.com
            </a>
            <a href="tel:+919840110602" className="hover:text-white transition-colors flex items-center gap-1.5" aria-label="Call Instrutech Solutions">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              +91 98401 10602
            </a>
            <span className="text-gray-400">Mon–Sat, 9:00 AM – 6:00 PM IST</span>
          </div>
          <Link href="/contact" className="bg-corp-blue hover:bg-corp-blue-light text-white px-3 py-1 rounded text-xs font-semibold transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
      {/* Main Header */}
      <header className={`sticky top-0 z-50 bg-white transition-shadow duration-200 ${scrolled ? 'shadow-md' : 'border-b border-corp-border'}`}>
        <nav className="max-w-container mx-auto px-4 h-16 flex items-center justify-between" role="navigation" aria-label="Main navigation">
          {/* Logo */}
          <Link href="/homepage" aria-label="Instrutech Solutions Home">
            <AppLogo size={48} text="Instrutech Solutions" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href || pathname?.startsWith(link?.href + '/');
              if (link?.label === 'Products') {
                return (
                  <div
                    key={link?.href}
                    className="relative mega-menu-trigger"
                    onMouseEnter={() => setMegaOpen(true)}
                    onMouseLeave={() => setMegaOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1 px-4 py-2 rounded text-sm font-semibold transition-colors ${isActive ? 'text-corp-blue bg-corp-blue-muted' : 'text-corp-text hover:text-corp-blue hover:bg-corp-surface'}`}
                      aria-expanded={megaOpen}
                      aria-haspopup="true"
                    >
                      {link?.label}
                      <svg className={`w-4 h-4 transition-transform ${megaOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {/* Mega Menu */}
                    {megaOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-[680px] bg-white border border-corp-border shadow-xl rounded-b-lg p-6 grid grid-cols-2 gap-2 z-50">
                        <div className="col-span-2 mb-2 pb-2 border-b border-corp-border">
                          <span className="text-xs font-bold uppercase tracking-wider text-corp-muted">Product Categories</span>
                        </div>
                        {categories?.map((cat) => (
                          <Link
                            key={cat?.id}
                            href={`/products`}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-corp-surface group transition-colors"
                          >
                            <div className="w-8 h-8 bg-corp-blue-muted rounded flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-corp-blue transition-colors">
                              <svg className="w-4 h-4 text-corp-blue group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-corp-text group-hover:text-corp-blue transition-colors leading-tight">{cat?.name}</div>
                              <div className="text-xs text-corp-muted mt-0.5">{cat?.productCount} products</div>
                            </div>
                          </Link>
                        ))}
                        <div className="col-span-2 mt-2 pt-2 border-t border-corp-border">
                          <Link href="/products" className="text-sm font-semibold text-corp-blue hover:underline flex items-center gap-1">
                            View All Products
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${isActive ? 'text-corp-blue bg-corp-blue-muted' : 'text-corp-text hover:text-corp-blue hover:bg-corp-surface'}`}
                >
                  {link?.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/contact"
              className="bg-corp-blue hover:bg-corp-blue-dark text-white px-5 py-2.5 rounded text-sm font-bold transition-colors shadow-sm"
            >
              Request Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-corp-text hover:text-corp-blue transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-corp-border shadow-lg">
            <div className="max-w-container mx-auto px-4 py-4 space-y-1">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={link?.href}
                    href={link?.href}
                    className={`block px-4 py-3 rounded text-sm font-semibold transition-colors ${isActive ? 'text-corp-blue bg-corp-blue-muted' : 'text-corp-text hover:bg-corp-surface'}`}
                  >
                    {link?.label}
                  </Link>
                );
              })}
              <div className="pt-3 border-t border-corp-border">
                <div className="text-xs text-corp-muted mb-2 px-4">Contact</div>
                <a href="tel:+911140001234" className="block px-4 py-2 text-sm text-corp-text hover:text-corp-blue">+91 98401 10602</a>
                <a href="mailto:instrutechsouth@gmail.com" className="block px-4 py-2 text-sm text-corp-text hover:text-corp-blue">instrutechsouth@gmail.com</a>
              </div>
              <div className="pt-2">
                <Link href="/contact" className="block w-full bg-corp-blue text-white text-center py-3 rounded font-bold text-sm hover:bg-corp-blue-dark transition-colors">
                  Request Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}